"use client";

import { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";

const QUAD_VS = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const SKY_FS = `
#ifdef GL_OES_standard_derivatives
#extension GL_OES_standard_derivatives : enable
#endif
precision highp float;
varying vec2 v_uv;

uniform float u_time;
uniform vec2 u_res;
uniform float u_light_mode;

#define PI 3.14159265359
#define TAU 6.28318530718
#define DENSITY 0.6

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = rot * p * 2.0;
    a *= 0.5;
  }
  return v;
}

float starField(vec2 uv, float scale, float threshold) {
  vec2 scaled = uv * scale;
  vec2 id = floor(scaled);
  vec2 gv = fract(scaled) - 0.5;
  float stars = 0.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 offset = vec2(float(x), float(y));
      vec2 cellId = id + offset;
      float h = hash(cellId);
      if (h > threshold) {
        vec2 starPos = vec2(hash(cellId + 10.0), hash(cellId + 20.0)) - 0.5;
        float d = length(gv - offset - starPos);
        float brightness = hash(cellId + 30.0);
        float size = 0.04 + brightness * 0.05;
        float star = exp(-d * d / (size * size));
        stars += star * brightness;
      }
    }
  }
  return stars;
}

float milkyWay(vec2 uv) {
  float angle = 0.38;
  float ca = cos(angle), sa = sin(angle);
  vec2 ruv = vec2(ca * uv.x - sa * uv.y, sa * uv.x + ca * uv.y);
  float band = exp(-ruv.y * ruv.y * 12.0);
  float detail = fbm(uv * 6.0) * 0.4 + 0.6;
  float dust = fbm(uv * 12.0);
  float dark = fbm(uv * 18.0 + 5.0);
  float mw = band * detail;
  mw *= (1.0 - dark * 0.6);
  mw *= smoothstep(0.35, 0.75, dust + 0.3);
  return mw * 0.12;
}

vec3 horizonGlow(vec2 uv) {
  float t = u_time * 0.08;
  float cycle = sin(t) * 0.5 + 0.5;
  float glow = exp(-uv.y * 3.5) * 0.15;
  vec3 warmGlow = mix(
    vec3(0.06, 0.03, 0.05),
    vec3(0.14, 0.06, 0.02),
    cycle
  );
  return warmGlow * glow;
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_res.x / u_res.y;
  vec2 centered = (uv - 0.5) * vec2(aspect, 1.0);

  float rot = u_time * 0.0216;
  float cr = cos(rot), sr = sin(rot);
  vec2 pole = vec2(0.06, 0.48);
  vec2 p = centered - pole;
  vec2 rotated = vec2(cr * p.x - sr * p.y, sr * p.x + cr * p.y);
  vec2 skyUV = rotated + pole;

  float breathe = sin(u_time * 0.015) * 0.5 + 0.5;
  vec3 col = mix(vec3(0.008, 0.008, 0.022), vec3(0.012, 0.008, 0.020), breathe);
  col += vec3(0.005, 0.01, 0.025) * (1.0 - uv.y * 0.7);

  float mw = milkyWay(skyUV);
  col += mw * mix(vec3(0.6, 0.65, 0.9), vec3(0.8, 0.7, 0.6), fbm(skyUV * 4.0));

  float threshold = 1.0 - 0.12 * DENSITY;
  float s1 = starField(skyUV, 80.0, threshold);
  float s2 = starField(skyUV, 160.0, threshold + 0.08);
  float s3 = starField(skyUV, 320.0, threshold + 0.16);
  float stars = s1 + s2 * 0.6 + s3 * 0.3;

  vec3 starCol = vec3(0.85, 0.9, 1.0);
  float colorVar = hash(floor(skyUV * 80.0));
  if (colorVar > 0.7) starCol = vec3(1.0, 0.85, 0.7);
  if (colorVar > 0.9) starCol = vec3(0.7, 0.8, 1.0);
  col += stars * starCol;

  col += horizonGlow(uv);

  col += vec3(0.08, 0.02, 0.06) * fbm(skyUV * 2.0 + 10.0) * 0.04;
  col += vec3(0.02, 0.04, 0.08) * fbm(skyUV * 2.5 + 20.0) * 0.035;

  float horizonFade = smoothstep(0.0, 0.3, uv.y);
  col *= horizonFade;

  float mistY = (uv.y - 0.05) * 8.0;
  float mistBand = exp(-mistY * mistY * 1.5) * step(0.0, uv.y - 0.03);
  float mistNoise = fbm(vec2(uv.x * 5.0 + u_time * 0.008, uv.y * 3.0));
  float mistWisp = fbm(vec2(uv.x * 12.0 - u_time * 0.012, uv.y * 8.0 + 3.0));
  col += vec3(0.035, 0.028, 0.02) * mistBand * mistNoise * 0.6 * (0.5 + mistWisp * 0.5);

  float haze = exp(-uv.y * 5.0) * 0.06;
  col += vec3(0.03, 0.022, 0.018) * haze / max(horizonFade, 0.05);

  float treeline = 0.05 + 0.03 * sin(uv.x * 25.0) + 0.02 * sin(uv.x * 60.0 + 1.0) + 0.01 * sin(uv.x * 130.0);
  treeline += 0.015 * noise(vec2(uv.x * 50.0, 0.0));
  float trees = smoothstep(treeline + 0.04, treeline - 0.01, uv.y);
  col = mix(col, vec3(0.01, 0.009, 0.014), trees);

  col *= vec3(1.12, 1.04, 0.85);

  float luma = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(vec3(luma), col, 0.82);

  float vigDist = length((uv - 0.5) * vec2(1.1, 0.8));
  float vig = 1.0 - vigDist * vigDist * 1.2;
  vig = clamp(vig, 0.0, 1.0);
  vig = pow(vig, 1.4);
  col *= mix(vec3(0.02, 0.01, 0.005), vec3(1.0), vig);

  float g1 = hash(uv * u_res + 0.1);
  float g2 = hash(uv * u_res + 7.3);
  float grainLuma = (g1 + g2) * 0.5 - 0.5;
  col += vec3(grainLuma * 1.1, grainLuma, grainLuma * 0.8) * 0.025;

  if (u_light_mode > 0.5) {
    vec2 luv = uv * vec2(aspect, 1.0);
    float t = u_time * 0.8;

    // Near-white base
    vec3 lcol = vec3(0.99, 0.985, 0.975);

    // Three caustic layers at different scales, speeds, and directions
    float c1 = 0.0, c2 = 0.0, c3 = 0.0;
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      vec2 p1 = luv * (3.0 + fi * 1.5) + vec2(t * (0.2 + fi * 0.05), t * (0.1 - fi * 0.03));
      float n = noise(p1);
      n = noise(p1 + n * 0.8); // warp for organic shape
      float caustic = pow(1.0 - abs(sin(n * PI * 2.0 + t * (0.6 + fi * 0.2))), 5.0);
      if (i == 0) c1 = caustic;
      if (i == 1) c2 = caustic;
      if (i == 2) c3 = caustic;
    }

    float bright = c1 * 0.5 + c2 * 0.35 + c3 * 0.25;

    // Prismatic color — shifts based on position and time
    float hue = noise(luv * 2.0 + t * 0.05) * 2.0 + t * 0.08;
    vec3 prism = vec3(
      0.5 + 0.5 * sin(hue * TAU),
      0.5 + 0.5 * sin(hue * TAU + 2.094),
      0.5 + 0.5 * sin(hue * TAU + 4.189)
    );
    prism = mix(vec3(1.0), prism, 0.4);

    // Caustics add light, not subtract
    lcol += bright * prism * 0.15;

    // Soft shadow in non-caustic areas for depth
    lcol -= (1.0 - bright) * 0.03;

    // Vignette
    float vig = length((uv - 0.5) * vec2(aspect, 1.0));
    lcol -= vig * vig * 0.04;

    // Grain
    float lg = hash(uv * u_res + 0.1);
    lcol += (lg - 0.5) * 0.01;

    col = lcol;
  }

  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}
`;

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  src: string
): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
    return null;
  }
  return s;
}

function createProgram(
  gl: WebGLRenderingContext,
  vsSrc: string,
  fsSrc: string
): WebGLProgram | null {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  if (!vs || !fs) return null;
  const p = gl.createProgram();
  if (!p) return null;
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(p));
    return null;
  }
  return p;
}

export function NightSkyBackground({ className }: { className?: string }) {
  const { resolvedTheme } = useTheme();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lightModeRef = useRef(0);

  useEffect(() => {
    lightModeRef.current = resolvedTheme === "light" ? 1 : 0;
  }, [resolvedTheme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const glOpts = { antialias: true };
    const gl = (
      canvas.getContext("webgl2", glOpts) || canvas.getContext("webgl", glOpts)
    ) as WebGLRenderingContext | null;
    if (!gl) return;

    // Enable derivatives for WebGL 1
    gl.getExtension("OES_standard_derivatives");

    const program = createProgram(gl, QUAD_VS, SKY_FS);
    if (!program) return;

    const aPos = gl.getAttribLocation(program, "a_pos");
    const uTime = gl.getUniformLocation(program, "u_time");
    const uRes = gl.getUniformLocation(program, "u_res");
    const uLightMode = gl.getUniformLocation(program, "u_light_mode");

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    gl.useProgram(program);
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    let time = 0;
    let last = performance.now();
    let frameId = 0;
    let visible = true;

    const resize = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const dpr = Math.min(window.devicePixelRatio, 2);
      const w = wrap.offsetWidth;
      const h = wrap.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener("resize", resize);

    const frame = (now: number) => {
      if (!visible) {
        frameId = 0;
        return;
      }
      const dt = (now - last) * 0.001;
      last = now;
      time += dt * 0.4;
      gl.uniform1f(uTime, time);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uLightMode, lightModeRef.current);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      frameId = requestAnimationFrame(frame);
    };

    frameId = requestAnimationFrame(frame);

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !frameId) {
          last = performance.now();
          frameId = requestAnimationFrame(frame);
        }
      },
      { threshold: 0 }
    );
    const wrap = wrapRef.current;
    if (wrap) observer.observe(wrap);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} className={cn("overflow-hidden", className)}>
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}
